import { useEffect } from 'react';
import { X, FileText, CheckCircle2, FileCheck, Send, Clock, XCircle, Download, Banknote } from 'lucide-react';
import { useInvoice, useDownloadInvoicePdf } from '@/hooks/useInvoices';
import {
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS,
  type Invoice,
} from '@/types';
import { formatFCFA, formatMontant, formatNumber } from '@/utils/currency';
import { toast } from '@/stores/toastStore';

interface InvoiceDrawerProps {
  invoiceId: string | null;
  onClose:   () => void;
  onPay?:    (id: string) => void;
  onValidate?: (id: string) => void;
  onCancel?: (id: string) => void;
  /** TICKET-004 (C-01) : mentions OHADA du client manquantes → ouvrir sa fiche. */
  onFicheClient?: (contactId: string) => void;
}

const fmtDateLong = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

/**
 * Drawer pixel-perfect CTO-FRONT-002.
 * Référence : design-factures-v1.html lignes 469-600.
 * Panneau latéral droit avec amount-box gradient + info grid + lignes + timeline.
 */
export const InvoiceDrawer = ({ invoiceId, onClose, onPay, onValidate, onCancel, onFicheClient }: InvoiceDrawerProps) => {
  const { data: invoice, isLoading } = useInvoice(invoiceId ?? undefined);
  const downloadPdf = useDownloadInvoicePdf();

  const handlePdf = async (id: string) => {
    try {
      const { filename } = await downloadPdf.mutateAsync(id);
      toast.success('PDF téléchargé', filename);
    } catch (err: any) {
      const code = err?.code ?? 'PDF_ERROR';
      const msg  = err?.message ?? 'Impossible de générer le PDF';
      if (code === 'MISSING_LEGAL_EMITTER') {
        toast.error('Mentions légales émetteur manquantes', msg);
      } else if (code === 'MISSING_LEGAL_CLIENT') {
        toast.error('Mentions légales client manquantes', msg);
        if (invoice?.contactId) { onFicheClient?.(invoice.contactId); onClose(); }
      } else {
        toast.error('Échec de génération PDF', msg);
      }
    }
  };

  // Escape + body scroll lock
  useEffect(() => {
    if (!invoiceId) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [invoiceId, onClose]);

  if (!invoiceId) return null;

  return (
    <div
      className="ds-drawer-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <aside className="ds-drawer">
        {isLoading || !invoice ? (
          <div style={{ padding: 32, color: 'var(--tx-3)', fontSize: 13 }}>Chargement…</div>
        ) : (
          <DrawerContent
            invoice={invoice}
            onClose={onClose}
            onPay={onPay}
            onValidate={onValidate}
            onCancel={onCancel}
            onPrint={() => void handlePdf(invoice.id)}
            downloadPending={downloadPdf.isPending}
          />
        )}
      </aside>
    </div>
  );
};

const DrawerContent = ({
  invoice, onClose, onPay, onValidate, onCancel, onPrint, downloadPending,
}: {
  invoice:    Invoice;
  onClose:    () => void;
  onPay?:     (id: string) => void;
  onValidate?: (id: string) => void;
  onCancel?:  (id: string) => void;
  onPrint:    () => void;
  downloadPending?: boolean;
}) => {
  const totalCents    = Number(BigInt(invoice.totalCents));
  const paidCents     = Number(BigInt(invoice.paidAmountCents));
  const restCents     = totalCents - paidCents;
  const subtotalCents = Number(BigInt(invoice.subtotalCents));
  const taxCents      = Number(BigInt(invoice.taxCents));
  const remisePct     = parseFloat(invoice.remisePct ?? '0');
  const progressPct   = totalCents > 0 ? Math.round((paidCents / totalCents) * 100) : 0;

  const isProforma  = invoice.status === 'proforma';
  const isDraft     = invoice.status === 'draft';
  const isPaid      = invoice.status === 'paid';
  const isCancelled = invoice.status === 'cancelled';
  const isPayable   = !isProforma && !isPaid && !isCancelled;
  // MANQUE-01 : annulable si proforma ou brouillon sans écriture comptable
  const isCancellable = (isProforma || isDraft) && !invoice.journalEntryId;

  return (
    <>
      {/* Header gradient */}
      <header className="ds-drawer-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ds-dh-title">{invoice.contact?.companyName ?? invoice.contact?.fullName ?? '—'}</div>
          <div className="ds-dh-ref">{invoice.reference}{invoice.contact?.companyName && invoice.contact?.fullName ? ` · ${invoice.contact.fullName}` : ''}</div>
          <div className="ds-dh-badges">
            <span className="ds-dh-badge">{INVOICE_TYPE_LABELS[invoice.invoiceType]}</span>
            <span className="ds-dh-badge">{INVOICE_STATUS_LABELS[invoice.status]}</span>
            {invoice.version > 1 && <span className="ds-dh-badge">v{invoice.version}</span>}
            {remisePct > 0 && <span className="ds-dh-badge">−{remisePct}%</span>}
          </div>
        </div>
        <button type="button" className="ds-drawer-close" onClick={onClose} aria-label="Fermer">
          <X size={18} />
        </button>
      </header>

      <div className="ds-drawer-body">
        {/* Amount box */}
        <div className="ds-amount-box">
          <div className="ds-ab-item">
            <div className="ds-ab-label">Total TTC</div>
            <div className="ds-ab-val">{formatNumber(totalCents / 100)}</div>
            <div className="ds-ab-sub">{invoice.currency}</div>
          </div>
          <div className="ds-ab-item">
            <div className="ds-ab-label">Payé</div>
            <div className="ds-ab-val" style={{ color: paidCents > 0 ? 'rgba(110,231,183,1)' : '#fff' }}>{formatNumber(paidCents / 100)}</div>
            <div className="ds-ab-sub">{invoice.currency}</div>
          </div>
          <div className="ds-ab-item">
            <div className="ds-ab-label">Reste dû</div>
            <div className="ds-ab-val">{formatNumber(restCents / 100)}</div>
            <div className="ds-ab-sub">{invoice.currency}</div>
          </div>
        </div>

        <div className="ds-pay-progress">
          <div className="ds-pay-prog-head">
            <span style={{ color: 'var(--tx-2)', fontWeight: 500 }}>Progression du paiement</span>
            <span style={{ color: 'var(--tx-3)' }}>{formatFCFA(paidCents)} / {formatFCFA(totalCents)}</span>
          </div>
          <div className="ds-pay-prog-bar">
            <div className="ds-pay-prog-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Infos */}
        <div className="ds-drawer-section">
          <div className="ds-drawer-section-title">Informations facture</div>
          <div className="ds-info-grid">
            <div className="ds-info-item"><label>Date émission</label><span>{fmtDateLong(invoice.issueDate)}</span></div>
            <div className="ds-info-item"><label>Date échéance</label><span style={{ color: !isPaid ? 'var(--warning)' : undefined }}>{fmtDateLong(invoice.dueDate)}</span></div>
            <div className="ds-info-item"><label>Statut</label><span>{INVOICE_STATUS_LABELS[invoice.status]}</span></div>
            <div className="ds-info-item"><label>Devise</label><span className="mono">{invoice.currency}</span></div>
            <div className="ds-info-item full"><label>Tiers</label><span>
              {invoice.contact?.companyName ?? '—'}
              {invoice.contact?.fullName && invoice.contact?.companyName && ` · ${invoice.contact.fullName}`}
              {invoice.contact?.email && ` · ${invoice.contact.email}`}
            </span></div>
            {invoice.paidAt && (
              <div className="ds-info-item full"><label>Payée le</label><span style={{ color: 'var(--success)' }}>{fmtDateLong(invoice.paidAt)}</span></div>
            )}
            {invoice.paymentTerms && (
              <div className="ds-info-item full">
                <label>Conditions de paiement</label>
                <span style={{ whiteSpace: 'pre-wrap' }}>{invoice.paymentTerms}</span>
              </div>
            )}
          </div>
        </div>

        {/* Lignes */}
        <div className="ds-drawer-section">
          <div className="ds-drawer-section-title">Détail des prestations</div>
          <table className="ds-lignes-table">
            <thead>
              <tr>
                <th>Désignation</th>
                <th className="r">Qté</th>
                <th className="r">P.U HT</th>
                <th className="r">Total HT</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.designation}
                    {l.produit && <><br /><span style={{ fontSize: 11, color: 'var(--tx-3)' }}>{l.produit.code}</span></>}
                  </td>
                  <td className="r">{formatNumber(parseFloat(l.quantite))}</td>
                  <td className="r">{formatMontant(l.prixUnitaireCents)}</td>
                  <td className="r">{formatMontant(l.totalCents)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} style={{ textAlign: 'right' }}>{remisePct > 0 ? 'HT brut' : 'Total HT'}</td>
                <td className="r">{formatNumber(subtotalCents / 100)}</td>
              </tr>
              {remisePct > 0 && (
                <>
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'right', color: 'var(--accent)' }}>Remise {remisePct}%</td>
                    <td className="r" style={{ color: 'var(--accent)' }}>− {formatNumber(subtotalCents * remisePct / 100 / 100)}</td>
                  </tr>
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'right' }}>HT net</td>
                    <td className="r">{formatNumber((subtotalCents - subtotalCents * remisePct / 100) / 100)}</td>
                  </tr>
                </>
              )}
              <tr>
                <td colSpan={3} style={{ textAlign: 'right' }}>TVA</td>
                <td className="r">{formatNumber(taxCents / 100)}</td>
              </tr>
              <tr>
                <td colSpan={3} style={{ textAlign: 'right', color: 'var(--p)' }}>Total TTC</td>
                <td className="r" style={{ color: 'var(--p)' }}>{formatNumber(totalCents / 100)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Timeline */}
        <div className="ds-drawer-section">
          <div className="ds-drawer-section-title">Historique & suivi</div>
          <div className="ds-timeline">
            <TimelineItem
              icon="📤"
              ok
              title={`Facture créée (${isProforma ? 'proforma' : 'finale'})`}
              date={fmtDateLong(invoice.createdAt)}
            />
            {invoice.version > 1 && (
              <TimelineItem
                icon="✏️"
                ok
                title={`${invoice.version - 1} modification${invoice.version > 2 ? 's' : ''} proforma`}
                date={`Dernière mise à jour ${fmtDateLong(invoice.updatedAt)}`}
              />
            )}
            {isProforma ? (
              <TimelineItem
                icon={<Clock size={14} />}
                title="En attente de validation"
                date="Cliquez 'Valider' pour générer la facture finale"
              />
            ) : (
              <TimelineItem
                icon={<FileCheck size={14} />}
                ok
                title="Convertie en facture finale"
                date={`Nouvelle référence : ${invoice.reference}`}
              />
            )}
            {isPaid && invoice.paidAt && (
              <TimelineItem
                icon={<CheckCircle2 size={14} />}
                ok
                title="Paiement reçu"
                date={fmtDateLong(invoice.paidAt)}
              />
            )}
            {!isPaid && !isProforma && (
              <TimelineItem
                icon={<Clock size={14} />}
                title="Paiement attendu"
                date={`Avant le ${fmtDateLong(invoice.dueDate)}`}
              />
            )}
          </div>
        </div>
      </div>

      {/* Bandeau annulation si pièce déjà annulée */}
      {isCancelled && invoice.cancelReason && (
        <div style={{
          margin: '0 20px 12px', padding: 12, background: 'var(--bg-2, #fef2f2)',
          border: '1px solid var(--danger, #ef4444)', borderRadius: 6, fontSize: 12,
        }}>
          <div style={{ color: 'var(--danger, #ef4444)', fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <XCircle size={14} /> Pièce annulée{invoice.cancelledAt ? ` le ${fmtDateLong(invoice.cancelledAt)}` : ''}
          </div>
          <div style={{ color: 'var(--tx-2)' }}>Motif : {invoice.cancelReason}</div>
        </div>
      )}

      {/* Footer actions */}
      <div className="ds-drawer-actions">
        <button className="ds-btn ds-btn-g" onClick={onPrint} disabled={downloadPending} title="Télécharge le PDF OHADA depuis le serveur (mentions légales obligatoires)">
          {downloadPending ? <Clock size={14} className="animate-spin" /> : <Download size={14} />} {downloadPending ? 'Génération…' : 'Télécharger PDF'}
        </button>
        {isProforma && onValidate && (
          <button className="ds-btn ds-btn-p" onClick={() => onValidate(invoice.id)}>
            <FileCheck size={14} /> Valider facture finale
          </button>
        )}
        {isPayable && onPay && (
          <button className="ds-btn ds-btn-p" onClick={() => onPay(invoice.id)}>
            <Banknote size={14} /> Encaisser
          </button>
        )}
        {isCancellable && onCancel && (
          <button
            className="ds-btn ds-btn-g"
            onClick={() => onCancel(invoice.id)}
            style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
            title="Annuler la pièce (motif obligatoire)"
          >
            <XCircle size={14} /> Annuler la pièce
          </button>
        )}
        <button className="ds-btn ds-btn-g" disabled title="Bientôt">
          <Send size={14} /> Envoyer par email
        </button>
        <button className="ds-btn ds-btn-g" disabled title="Bientôt">
          <FileText size={14} /> Avoir
        </button>
      </div>
    </>
  );
};

const TimelineItem = ({
  icon, title, date, ok,
}: { icon: React.ReactNode; title: string; date: string; ok?: boolean }) => (
  <div className="ds-tl-item">
    <div className={`ds-tl-dot ${ok ? 'ok' : 'wait'}`}>{icon}</div>
    <div className="ds-tl-content">
      <div className="ds-tl-title" style={!ok ? { color: 'var(--tx-3)' } : undefined}>{title}</div>
      <div className="ds-tl-date">{date}</div>
    </div>
  </div>
);
